import { take, takeEvery, takeLatest, put, select, call, fork } from 'redux-saga/effects'

import * as grs from 'grs'
import { indicateProgress } from 'api'
import { userSelector } from 'auth/selectors'

import * as constants from './constants'
import * as selectors from './selectors'
import * as actions from './actions'
import { setGuestReservationsVehicleUpdatedState, updateVehicleError } from 'manage-guest-user-booking/actions'
import { guestUserReservationsSelector } from 'manage-guest-user-booking/selectors'
import getRandomness from 'shared/random-generator'
import featureToggles from 'shared/featureToggles'

const PAGE_SIZE = 10

export function* getReservationsSaga() {
    yield takeLatest(
        [constants.GET_RESERVATIONS, constants.GET_MORE_RESERVATIONS, constants.SET_STATUS_FILTER],
        function* ({ offsetSize, pageSize, forNpsSurvey }) {
            const offset = yield select(selectors.offsetSelector)
            const status = yield select(selectors.statusSelector)
            const requestId = getRandomness()

            yield put(
                grs.actions.getReservations(
                    offsetSize || offset,
                    pageSize || PAGE_SIZE,
                    forNpsSurvey ? undefined : status,
                    requestId
                )
            )
            const { result } = yield take(
                action => action.type === grs.constants.RECEIVED_RESERVATIONS && action.requestId === requestId
            )
            yield put(actions.setReservations(result))
        }
    )
}

export function* watchGetReservationCancelAmendPolicySaga() {
    yield takeLatest(constants.GET_RESERVATION_CANCEL_AMEND_POLICY, function* ({ reservationId }) {
        yield put(grs.actions.getReservationCancelAmendPolicy(reservationId))
        const { response } = yield take(grs.constants.RESERVATION_CANCEL_AMEND_POLICY_RESPONSE)
        yield put(actions.setCancelReservationValues(response))
    })
}

export function* cancelReservationSaga() {
    yield takeLatest(constants.CANCEL_RESERVATION, function* ({ reservationId }) {
        const { reservationId: confirmReservationId } = yield take(constants.CONFIRM_CANCEL_RESERVATION)
        if (reservationId === confirmReservationId) {
            yield put(grs.actions.cancelReservation(reservationId))
            const { response } = yield take(grs.constants.CANCEL_RESERVATION_RESPONSE)
            if (response instanceof Error) yield put(actions.cancelReservationFailed(response))
            else {
                yield put(actions.cancelReservationFinished())
                yield put(actions.closeReservationDetails())
                yield put(actions.setStatusFilter(grs.constants.STATUS_CANCELLED))
            }
        }
    })
}

const makeEntityUpdateObject = (reservationId, reservation, selectedVehicles) => ({
    [grs.schemas.reservationSchema.key]: {
        [reservationId]: {
            ...reservation,
            vehicles: selectedVehicles.map(vehicle => ({
                ...vehicle,
            })),
        },
    },
})

export function* changeVehicleSaga() {
    yield takeLatest(constants.CHANGE_VEHICLE, function* ({ reservationId, selectedVehicles }) {
        try {
            yield call(indicateProgress, { names: ['changeVehicle'], clearPending: true }, function* () {
                yield put(grs.actions.changeVehicle(reservationId, selectedVehicles))
                const { response } = yield take(grs.constants.CHANGE_VEHICLE_RESPONSE)
                const guestUserReservations = yield select(guestUserReservationsSelector)

                if (!(response instanceof Error)) {
                    const reservation = yield select(grs.selectors.reservationSelector(reservationId))
                    yield put(
                        actions.updateEntity(
                            reservationId,
                            makeEntityUpdateObject(reservationId, reservation, selectedVehicles)
                        )
                    )
                    //if this is guest user reservations flow, then set vehicle updated state as marker for next actions in a component
                    if (guestUserReservations) {
                        yield put(setGuestReservationsVehicleUpdatedState())
                    }
                } else yield put(updateVehicleError(response))
            })
        } catch (exception) {
            yield put(updateVehicleError(exception))
        }
    })
}

export function* changePaymentMethod() {
    yield takeLatest(constants.CHANGE_PAYMENT_METHOD, function* ({ reservationId }) {
        let exchangePaymentMethodResult
        try {
            yield indicateProgress(['changePaymentMethod'], function* () {
                const { token } = yield select(userSelector)
                exchangePaymentMethodResult = yield call(grs.api.exchangePaymentMethod, reservationId, token)
            })
            yield put(actions.setPaymentMethod(exchangePaymentMethodResult, undefined))
        } catch (error) {
            yield put(actions.setPaymentMethod(undefined, error.message))
        }
    })
}

export function* downloadReceiptSaga() {
    yield takeEvery(constants.DOWNLOAD_RECEIPT, function* ({ reservationId, receiptTab }) {
        yield put(grs.actions.getReservationReceipt(reservationId, receiptTab))
    })
}

export default function* rootSaga() {
    yield fork(getReservationsSaga)
    yield fork(watchGetReservationCancelAmendPolicySaga)
    yield fork(cancelReservationSaga)
    yield fork(changeVehicleSaga)
    yield fork(downloadReceiptSaga)
    yield fork(changePaymentMethod)
}
